import { CdkDrag, CdkDragEnd, CdkDragMove, CdkDragStart } from "@angular/cdk/drag-drop";
import { NgComponentOutlet, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    signal,
    TemplateRef,
    Type,
    viewChild,
    ViewEncapsulation
} from "@angular/core";
import { Environments, RaskRoller } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { environment } from "~environments/environment";
import { DashboardAdminComponent } from "./dashboard-admin/dashboard-admin.component";
import { CellDimension, DASHBOARD_CONFIG, WidgetData } from "./dashboard-config";
import { isColliding, isInside, isSameCell } from "./dashboard-utilities";
import { DashboardService } from "./dashboard.service";

@Component({
    selector: "rask-dashboard",
    imports: [
        NgComponentOutlet,
        NgTemplateOutlet,
        CdkDrag,
        DashboardAdminComponent
    ],
    templateUrl: "./dashboard.component.html",
    styleUrl: "./dashboard.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DashboardService],
    encapsulation: ViewEncapsulation.None
})
export class DashboardComponent {
    readonly dashboardConfig = inject(DASHBOARD_CONFIG);
    readonly data = model<WidgetData[]>(this.dashboardConfig.data);
    protected readonly preview = signal<CellDimension>(null);
    protected readonly cellGap = input<number>(this.dashboardConfig.cellGap);
    protected readonly columns = input<number>(this.dashboardConfig.columns);
    protected readonly rows = input<number>(this.dashboardConfig.rows);
    protected readonly locked = input<boolean>(false);
    protected readonly unregisteredErrorTemplate = input<TemplateRef<any>>();
    protected readonly cellWidth = computed<number>(() =>
        ((this.containerSize().width - this.cellGap()) / this.columns()) - this.cellGap());

    protected readonly cellHeight = computed<number>(() =>
        ((this.containerSize().height - this.cellGap()) / this.rows()) - this.cellGap());

    private readonly container = viewChild<ElementRef<HTMLDivElement>>("container");
    private readonly containerSize = signal<{ width: number; height: number }>({
        width: 0,
        height: 0
    });

    private resizeObserver: ResizeObserver;

    private readonly authenticationService = inject(AuthenticationService);

    get isDashboardAdmin(): boolean {
        return environment.environment !== Environments.Prod &&
            this.authenticationService.hasRole(RaskRoller.role.Systemadministrera);
    }

    constructor() {
        effect((onCleanup) => {
            this.setupResizeObserver();

            onCleanup(() => {
                this.resizeObserver?.disconnect();
            });
        });
    }

    protected getWidgetComponent(key: string): Type<any> {
        return this.dashboardConfig.widgets?.[key];
    }

    protected getRenderDimensions(cell: CellDimension): CellDimension {
        return {
            row: cell.row * (this.cellHeight() + this.cellGap()) + this.cellGap(),
            column: cell.column * (this.cellWidth() + this.cellGap()) + this.cellGap(),
            width: cell.width * this.cellWidth() + this.cellGap() * (cell.width - 1),
            height: cell.height * this.cellHeight() + this.cellGap() * (cell.height - 1)
        };
    }

    protected onDragStarted(_event: CdkDragStart, widget: WidgetData) {
        this.preview.set({
            ...widget.dimensions
        });
    }

    protected onDragMoved(event: CdkDragMove, widget: WidgetData) {
        const dropzone = this.getDropzone(widget.dimensions, event.distance);
        if (this.isDropzoneValid(dropzone, widget.dimensions)) {
            this.preview.set(dropzone);
        }
    }

    protected onDragEnded(event: CdkDragEnd, widget: WidgetData) {
    // Prevent Cdk from moving the element
        event.source.reset();
        const target = this.preview();
        this.updateWidget({
            ...widget,
            dimensions: { ...target }
        });
        this.preview.set(null);
    }

    private updateWidget(updated: WidgetData) {
        const widgets = this.data();
        const index = widgets.findIndex(w => w.id === updated.id);
        this.data.set([
            ...widgets.slice(0, index),
            updated,
            ...widgets.slice(index + 1)
        ]);
    }

    private getDropzone(source: CellDimension, { x = 0, y = 0 }): CellDimension {
        const { column, row } = source;
        const newColumn = column + Math.round(x / (this.cellWidth() + this.cellGap()));
        const newRow = row + Math.round(y / (this.cellHeight() + this.cellGap()));

        return {
            ...source,
            column: newColumn,
            row: newRow
        };
    }

    private isDropzoneValid(dropzone: CellDimension, source: CellDimension): boolean {
        const widgets = this.data();
        const containerDimensions: CellDimension = {
            row: 0,
            column: 0,
            width: this.columns(),
            height: this.rows()
        };

        // dropzone is out of bounds
        if (!isInside(dropzone, containerDimensions)) {
            return false;
        }

        // dropzone is colliding with another widget
        if (widgets.some(w => !isSameCell(source, w.dimensions) && isColliding(dropzone, w.dimensions))) {
            return false;
        }
        return true;
    }

    private setupResizeObserver() {
        if (!this.container() || this.resizeObserver) {
            return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                this.containerSize.set({
                    width,
                    height
                });
            }
        });
        this.resizeObserver.observe(this.container().nativeElement);
    }
}
